/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Col, Divider, Form, Input, Row, Space, Typography } from "antd";
import { notificationController } from "components/Common/Notification/Notification";
import Heading from "components/Common/Heading/Heading";
import "./Login.scss";
import { loginUser } from "api/services/userAuth.api";
import { UserData } from "interfaces/ApiInterface/ILoginFormData";
import { useDispatch } from "react-redux";
import { ROUTES_PATH } from "routes/Routes.Constant";
import { setUserDetail } from "store/slices/user.slice";
import { useAppSelector } from "hooks/reduxHooks";
//import { useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { getAuthorizationToken } from "api/services/dashboard.api";
import { setAuthToken } from "api/http.api";

const { Text } = Typography;

// eslint-disable-next-line max-lines-per-function
export const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const localPortalData: any = useAppSelector((state) => state.landingPage.lockerPortalDetails);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const redirectUrl = queryParams.get("redirect_url");
  const decodedRedirectUrl = redirectUrl ? decodeURIComponent(redirectUrl) : "";

  // Handler for navigating to registration page
  const handleRegisterClick = () => {
    const saveConfigUrl = (decodedRedirectUrl) ? `?redirect_url=${decodedRedirectUrl}` : "";
    navigate(`${ROUTES_PATH.REGISTRATION}${saveConfigUrl}`);
  };

  // Handler for canceling login and navigating back to home page
  const handleCancel = () => {
    navigate(ROUTES_PATH.HOMEPAGE);
  };

  // Handler for form submission
  const handleSubmit = async (values: UserData) => {
    const portalId: number | null = localPortalData?.lockerPortalDetails?.PortalId || null;
    if (portalId) {
      setLoading(true);
      await loginUser(values, portalId)
        .then((response) => {
          const userData = response?.User;
          dispatch(setUserDetail(userData));
          localStorage.setItem("UserRecord", JSON.stringify(userData));
          notificationController.success({
            message: "You are successfully logged in.",
          });
          setLoading(false);
          if(decodedRedirectUrl){
            navigate(decodedRedirectUrl);
          }else{
            navigate(ROUTES_PATH.HOMEPAGE);
          }

          if (process.env.REACT_APP_ENABLE_TOKEN_BASED_AUTHORIZATION) {
            getAuthorizationToken().then((res) => {
              setAuthToken(res.data.Response);
              sessionStorage.setItem("AuthToken", res.data.Response);
            });
          }
        })
        .catch((error) => {
          const errorCode = error?.response?.data?.ErrorCode;
          if (errorCode === 5) {
            notificationController.error({
              message: "Invalid email address",
            });
          } else {
            notificationController.error({
              message: "Invalid username/email or password",
            });
          }
        });
      setLoading(false);
    }
  };

  return (
    <div className="page-content-wrapper  p-30">
      <Row gutter={15} className="login-form-container">
        <Col span={12} xs={24} md={12} xxl={12} lg={12}>
          <Heading titleText="Login" titleClass="login-heading" />
          <div className="form-wrapper">
            <Form
              form={form}
              layout="vertical"
              name="login"
              initialValues={{ remember: true }}
              onFinish={handleSubmit}
              autoComplete="off"
            >
              <Form.Item
                label={
                  <span className="wrapper">
                    Username/Email <span className="text-danger">*</span>
                  </span>
                }
                name="username"
                rules={[
                  { required: true, message: "Username/Email address is required." },
                  { type: "email", message: "Please enter a valid Username/Email address." },
                ]}
                className="input-label"
              >
                <Input className="form-input" data-test-selector="txtLoginUsername" />
              </Form.Item>

              <Form.Item
                label={
                  <span className="wrapper">
                    Password <span className="text-danger">*</span>
                  </span>
                }
                name="password"
                rules={[{ required: true, message: "Password is required." }]}
                className="input-label"
              >
                <Input.Password className="form-input" data-test-selector="txtLoginPassword" />
              </Form.Item>

              <Form.Item className="login-action">
                <Space size={"middle"}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    className={isLoading ? "btn-disabled login-btn" : "login-btn"}
                    data-test-selector="btnSubmit"
                  >
                    Submit
                  </Button>
                  <Button
                    className="cancel-btn"
                    data-test-selector="btnCancel"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
            <div className="forgot-password">
              <Link
                to={ROUTES_PATH.FORGOT_PASSWORD}
                className="login-form-forgot"
                data-test-selector="linkLoginForgotPassword"
              >
                Forgot your Password?
              </Link>
            </div>
          </div>
        </Col>
      </Row>
      <Divider />
      <Row gutter={15} className="new-registration-container">
        <Col span={12} xs={24} md={12} lg={12} xxl={12}>
          <Space direction="vertical" size={"middle"}>
            <Heading titleText="New Customer?" titleClass="customer-header" />
            <Text className="new-registration-info">
              Sign-up for a free account to create your own custom product designs.
            </Text>
            <Button
              className="registration-btn"
              type="primary"
              data-test-selector={"btnRegister"}
              onClick={handleRegisterClick}
            >
              Register
            </Button>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default Login;